import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { normalizeNetworks } from "../../helpers/normalizeNetworks";
import useAuth from "../../routes/login/AuthContext";
import { GetAllNetworkInfo, Network } from "../../services/NetworkApi";
import { getAdminType, pushNetworkDetailsToDataLayer } from "../../helpers/analytics";

import uniq from "ramda/src/uniq";
import prop from "ramda/src/prop";
import { sortByL1Name } from "../../helpers/sortByL1Name";

interface INetworksContext {
  networks: Network[];
  isLoading: boolean;
  fetchNetworks: (isShallow?: boolean) => Promise<Network[] | undefined>;
  setNetworks: React.Dispatch<React.SetStateAction<Network[]>>;
}

const NetworksContext = createContext<INetworksContext | null>(null);

export const NetworksProvider: React.FC = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [networks, setNetworks] = useState<Network[]>([]);
  const { getAuthToken, isSuperAdmin } = useAuth();

  const normalizedNetworks = normalizeNetworks(networks);

  const fetchNetworks = useCallback(
    async (isShallow = false) => {
      if (!isShallow) {
        setIsLoading(true);
      }

      const networksIds = isShallow ? uniq(networks.map(prop("l1Id"))) : [];

      // @ts-ignore
      const res = await GetAllNetworkInfo(networksIds, await getAuthToken());

      // An empty list is a valid state (an enabled admin with no networks yet) —
      // leave the list empty and let the dashboard show an empty state instead
      // of signing the user out.
      if (res?.networks?.length) {
        setNetworks(sortByL1Name(res.networks));
      }

      setIsLoading(false);

      return res?.networks;
    },
    [normalizedNetworks]
  );


  // Push initial network values for Google Tag Manager when networks are loaded.
  useEffect(() => {
    if (networks.length > 0) {
      const selectedId = Number(localStorage.getItem("selectedNetworkId"));
      const network = networks.find((n) => n.l1Id === selectedId) || networks[0];
      const adminType = getAdminType(isSuperAdmin());

      pushNetworkDetailsToDataLayer(network?.portfolio?.name, network?.l1Name, adminType);
    }
  }, [networks, isSuperAdmin]);

  const value = useMemo(
    () => ({
      networks,
      isLoading,
      fetchNetworks,
      setNetworks,
    }),
    [normalizedNetworks, isLoading]
  );

  return (
    <NetworksContext.Provider value={value}>
      {props.children}
    </NetworksContext.Provider>
  );
};

export function useNetworksContext() {
  const context = useContext(NetworksContext);

  if (!context) {
    throw new Error("useNetworksContext must be inside NetworksProvider");
  }

  return context;
}
